import { createContext, useContext, useEffect, useState } from "react";
import authService from "./auth.service";
import { userRoles } from "../api/users.service";
import { setAuthState } from "./auth.state";
import { setDefaultVertical } from "../api/vertical.service";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize account from MSAL once and process redirect responses
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await authService.ensureInitialized();
      } catch (err) {
        console.error("Auth initialization failed", err);
      }

      const active = authService.getActiveAccount();
      setAccount(active || null);
      setLoading(false);
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    setAuthState({
      account,
      roles,
      isSignedIn: !!account,
      isUserAdmin
    });
  }, [account, isUserAdmin, roles]);

  const checkUserAdmin = async () => {
    if (!account) {
      setIsUserAdmin(false);
      return;
    }

    const isAdmin = await authService.isCurrentUserAdmin();
    setIsUserAdmin(isAdmin);
  }

  const refreshRoles = async () => {
    if (!account) {
      setRoles([]);
      return;
    }

    try {
      const res = await userRoles(account.idTokenClaims.email);
      const roles = res.data.sort((a, b) => a.roleId - b.roleId).map(r => r.roleName);
      if (isUserAdmin) {
        roles.push("fulcrum-admins");
      }
      setRoles(roles);
    } catch (err) {
      console.error("Error fetching roles", err);
      setRoles([]);
    }
  };

  useEffect(() => {
    if (!account) {
      setIsUserAdmin(false);
      return;
    }
    checkUserAdmin();
  }, [account]);

  useEffect(() => {
    if (!account) {
      setRoles([]);
      setIsUserAdmin(false);
      return;
    }
    refreshRoles();
    checkUserAdmin();
  }, [account, isUserAdmin]);

  useEffect(() => {
    if (roles.length > 0) {
      setDefaultVertical();
    }
  }, [roles]);

  const value = {
    account,
    roles,
    loading,
    isSignedIn: !!account,
    isUserAdmin,
    login: async () => {
      const result = await authService.login();
      setAccount(result.account || authService.getActiveAccount());
      await refreshRoles();
    },
    logout: async () => {
      await authService.logout();
      setAccount(null);
      setRoles([]);
    },
    refreshRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
